import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { PencilSquareIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';

interface Banner {
  _id: string;
  imageUrl: string;
  linkType: 'external' | 'internal';
  externalUrl?: string;
  internalRoute?: string;
  internalPayload?: Record<string, unknown>;
  order: number;
  active: boolean;
  title?: string;
  description?: string;
}

export default function BannersTable() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [banners, setBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBanners();
  }, []);

  const loadBanners = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/banners');
      setBanners(response.data.data || []);
    } catch (error) {
      console.error('Error loading banners:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t('banners.confirmDelete'))) return;
    try {
      await api.delete(`/admin/banners/${id}`);
      loadBanners();
    } catch (error: any) {
      alert(error.response?.data?.message || t('banners.failedToDelete'));
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center py-12 text-gray-500">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">{t('banners.title')}</h1>
        <button
          onClick={() => navigate('/banners/new')}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-orange text-white rounded-lg hover:bg-orange-600 transition-colors"
        >
          <PlusIcon className="w-5 h-5" />
          {t('banners.addBanner')}
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('banners.preview')}</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('banners.link')}</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('banners.order')}</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('banners.active')}</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('banners.actions')}</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {banners.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                  {t('banners.noBannersDescription')}
                </td>
              </tr>
            ) : (
              banners.map((b) => (
                <tr key={b._id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="w-32 h-14 rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                      <img
                        src={b.imageUrl}
                        alt=""
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = '';
                          (e.target as HTMLImageElement).style.backgroundColor = '#f3f4f6';
                        }}
                      />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {b.linkType === 'external' && b.externalUrl && (
                      <a href={b.externalUrl} target="_blank" rel="noopener noreferrer" className="text-primary-orange hover:underline truncate block max-w-[200px]">
                        {b.externalUrl}
                      </a>
                    )}
                    {b.linkType === 'internal' && (
                      <span className="text-gray-600">
                        {b.internalRoute}
                        {b.internalPayload && Object.keys(b.internalPayload).length > 0 && (
                          <span className="text-gray-400 ml-1">({JSON.stringify(b.internalPayload)})</span>
                        )}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{b.order}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${b.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                      {b.active ? t('banners.yes') : t('banners.no')}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-3">
                      <button
                        onClick={() => navigate(`/banners/${b._id}/edit`)}
                        className="p-2 text-gray-500 hover:text-primary-orange hover:bg-orange-50 rounded-lg"
                        title={t('common.edit')}
                      >
                        <PencilSquareIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(b._id)}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        title={t('common.delete')}
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
